/**
 * Telnyx WebRTC Client Wrapper
 *
 * Provides a clean interface for making calls using the Telnyx WebRTC SDK.
 */

import { TelnyxRTC } from '@telnyx/webrtc';

export type CallState =
  | 'idle'
  | 'connecting'
  | 'ringing'
  | 'active'
  | 'held'
  | 'done'
  | 'purge'
  | 'destroy';

export interface CallEventListeners {
  onStateChange?: (state: CallState) => void;
  onReady?: () => void;
  onError?: (error: Error) => void;
  onCallCreated?: (call: any) => void;
  onCallUpdated?: (call: any) => void;
  onCallEnded?: (call: any) => void;
}

/**
 * TelnyxClient wraps the Telnyx WebRTC SDK for making browser-based calls.
 */
export class TelnyxClient {
  private client: TelnyxRTC | null = null;
  private currentCall: any = null;
  private listeners: CallEventListeners = {};

  constructor(listeners?: CallEventListeners) {
    this.listeners = listeners || {};
  }

  /**
   * Connect to Telnyx using a JWT token.
   * The token is generated by the backend from the SIP connection credentials.
   */
  async connect(token: string): Promise<void> {
    try {
      console.log('📞 [AUDIO DEBUG] Initializing TelnyxRTC with JWT token (MediaStreams will be connected programmatically)');

      this.client = new TelnyxRTC({
        login_token: token,
        // Use publicly hosted ringback tone for 180 Ringing responses
        // (183 Session Progress with early media will override this)
        ringbackFile: '/ringback.wav',
      });

      console.log('📞 [AUDIO DEBUG] TelnyxRTC instance created');

      // Set up event listeners
      this.client.on('telnyx.ready', () => {
        console.log('📞 [AUDIO DEBUG] Telnyx client ready - signaling connection established');
        this.listeners.onReady?.();
      });

      this.client.on('telnyx.error', (error: any) => {
        console.error('📞 [AUDIO DEBUG] Telnyx error:', error);
        this.listeners.onError?.(new Error(error.message || 'Telnyx error'));
      });

      this.client.on('telnyx.socket.close', () => {
        console.log('📞 [AUDIO DEBUG] Telnyx socket closed');
      });

      // Listen for media-related events
      this.client.on('telnyx.mediaReady', (media: any) => {
        console.log('📞 [AUDIO DEBUG] Media ready event:', media);
      });

      this.client.on('telnyx.mediaError', (error: any) => {
        console.error('📞 [AUDIO DEBUG] Media error event:', error);
      });

      // Set up call notification listener
      this.client.on('telnyx.notification', (notification: any) => {
        console.log('📞 [AUDIO DEBUG] Notification received:', notification.type, notification);

        if (notification.type === 'callUpdate') {
          const call = notification.call;
          console.log('📞 [AUDIO DEBUG] Call state changed:', call.state);
          console.log('📞 [AUDIO DEBUG] Call object:', call);

          this.listeners.onStateChange?.(call.state as CallState);
          this.listeners.onCallUpdated?.(call);

          if (call.state === 'done' || call.state === 'purge' || call.state === 'destroy') {
            this.listeners.onCallEnded?.(call);
            this.currentCall = null;
          }
        }
      });

      // Connect to Telnyx
      console.log('📞 [AUDIO DEBUG] Calling client.connect()...');
      await this.client.connect();
      console.log('📞 [AUDIO DEBUG] Connected to Telnyx signaling server');

      // Enable microphone for calls
      console.log('📞 [AUDIO DEBUG] Calling enableMicrophone()...');
      await this.client.enableMicrophone();
      console.log('📞 [AUDIO DEBUG] enableMicrophone() completed');

      // Check if we have access to audio devices
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
        console.log(`📞 [AUDIO DEBUG] Audio devices - Inputs: ${audioInputs.length}, Outputs: ${audioOutputs.length}`);
        console.log('📞 [AUDIO DEBUG] Audio inputs:', audioInputs);
        console.log('📞 [AUDIO DEBUG] Audio outputs:', audioOutputs);
      }
    } catch (error) {
      console.error('📞 Failed to connect to Telnyx:', error);
      throw error;
    }
  }

  /**
   * Connect to Telnyx using username/password credentials.
   * Simpler authentication method for SIP connections.
   */
  async connectWithCredentials(username: string, password: string, micId?: string, speakerId?: string): Promise<void> {
    try {
      console.log('📞 [AUDIO DEBUG] Initializing TelnyxRTC (MediaStreams will be connected programmatically)');

      this.client = new TelnyxRTC({
        login: username,
        password: password,
        // Use publicly hosted ringback tone for 180 Ringing responses
        // (183 Session Progress with early media will override this)
        ringbackFile: '/ringback.wav',
      });

      console.log('📞 [AUDIO DEBUG] TelnyxRTC instance created');

      // Set up event listeners
      this.client.on('telnyx.ready', () => {
        console.log('📞 [AUDIO DEBUG] Telnyx client ready - signaling connection established');
        this.listeners.onReady?.();
      });

      this.client.on('telnyx.error', (error: any) => {
        console.error('📞 [AUDIO DEBUG] Telnyx error:', error);
        this.listeners.onError?.(new Error(error.message || 'Telnyx error'));
      });

      this.client.on('telnyx.socket.close', () => {
        console.log('📞 [AUDIO DEBUG] Telnyx socket closed');
      });

      // Listen for media-related events
      this.client.on('telnyx.mediaReady', (media: any) => {
        console.log('📞 [AUDIO DEBUG] Media ready event:', media);
      });

      this.client.on('telnyx.mediaError', (error: any) => {
        console.error('📞 [AUDIO DEBUG] Media error event:', error);
      });

      // Set up call notification listener
      this.client.on('telnyx.notification', (notification: any) => {
        console.log('📞 [AUDIO DEBUG] Notification received:', notification.type, notification);

        if (notification.type === 'callUpdate') {
          const call = notification.call;
          console.log('📞 [AUDIO DEBUG] Call state changed:', call.state);
          console.log('📞 [AUDIO DEBUG] Call object:', call);

          this.listeners.onStateChange?.(call.state as CallState);
          this.listeners.onCallUpdated?.(call);

          if (call.state === 'done' || call.state === 'purge' || call.state === 'destroy') {
            this.listeners.onCallEnded?.(call);
            this.currentCall = null;
          }
        }
      });

      // Connect to Telnyx
      console.log('📞 [AUDIO DEBUG] Calling client.connect()...');
      await this.client.connect();
      console.log('📞 [AUDIO DEBUG] Connected to Telnyx signaling server');

      // Set audio devices BEFORE enabling microphone
      if (micId || speakerId) {
        const settings: any = {};
        if (micId) {
          settings.micId = micId;
          settings.echoCancellation = true;
          console.log('📞 [AUDIO DEBUG] Setting microphone to:', micId);
        }
        if (speakerId) {
          settings.speakerId = speakerId;
          console.log('📞 [AUDIO DEBUG] Setting speaker to:', speakerId);
        }
        this.client.setAudioSettings(settings);
      }

      // Enable microphone for calls
      console.log('📞 [AUDIO DEBUG] Calling enableMicrophone()...');
      await this.client.enableMicrophone();
      console.log('📞 [AUDIO DEBUG] enableMicrophone() completed');

      // Check if we have access to audio devices
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
        console.log(`📞 [AUDIO DEBUG] Audio devices - Inputs: ${audioInputs.length}, Outputs: ${audioOutputs.length}`);
        console.log('📞 [AUDIO DEBUG] Audio inputs:', audioInputs);
        console.log('📞 [AUDIO DEBUG] Audio outputs:', audioOutputs);
      }
    } catch (error) {
      console.error('📞 [AUDIO DEBUG] Failed to connect to Telnyx:', error);
      throw error;
    }
  }

  /**
   * Make an outbound call.
   */
  async makeCall(destination: string, callerNumber: string): Promise<void> {
    if (!this.client) {
      throw new Error('Not connected to Telnyx. Call connect() first.');
    }

    try {
      console.log(`📞 [AUDIO DEBUG] Creating new call: ${callerNumber} → ${destination}`);

      this.currentCall = this.client.newCall({
        destinationNumber: destination,
        callerNumber: callerNumber,
        audio: true,  // Explicitly enable audio for the call
        video: false, // Explicitly disable video
      });

      console.log('📞 [AUDIO DEBUG] Call object created:', this.currentCall);
      console.log('📞 [AUDIO DEBUG] Call ID:', this.currentCall?.id);
      console.log('📞 [AUDIO DEBUG] Call state:', this.currentCall?.state);

      // Note: Call state changes are handled via telnyx.notification listener
      // set up in connect() or connectWithCredentials()

      this.listeners.onCallCreated?.(this.currentCall);
      console.log('📞 [AUDIO DEBUG] Call initiated successfully');
    } catch (error) {
      console.error('📞 [AUDIO DEBUG] Failed to make call:', error);
      throw error;
    }
  }

  /**
   * Hang up the current call.
   */
  hangup(): void {
    if (this.currentCall) {
      try {
        this.currentCall.hangup();
        console.log('📞 Call hung up');
      } catch (error) {
        console.error('📞 Failed to hang up:', error);
      }
    }
  }

  /**
   * Mute the microphone.
   */
  mute(): void {
    if (this.currentCall) {
      try {
        this.currentCall.muteAudio();
        console.log('📞 Audio muted');
      } catch (error) {
        console.error('📞 Failed to mute:', error);
      }
    }
  }

  /**
   * Send a DTMF tone (keypad digit) during an active call.
   */
  sendDTMF(digit: string): void {
    if (this.currentCall) {
      try {
        this.currentCall.dtmf(digit);
        console.log('📞 DTMF sent:', digit);
      } catch (error) {
        console.error('📞 Failed to send DTMF:', error);
      }
    }
  }

  /**
   * Unmute the microphone.
   */
  unmute(): void {
    if (this.currentCall) {
      try {
        this.currentCall.unmuteAudio();
        console.log('📞 Audio unmuted');
      } catch (error) {
        console.error('📞 Failed to unmute:', error);
      }
    }
  }

  /**
   * Check if currently on a call.
   */
  isOnCall(): boolean {
    return this.currentCall !== null;
  }

  /**
   * Get the current call state.
   */
  getCallState(): CallState | null {
    return this.currentCall?.state || null;
  }

  /**
   * Set audio input device (microphone).
   */
  setMicrophone(deviceId: string): void {
    if (this.client) {
      try {
        this.client.setAudioSettings({
          micId: deviceId,
          echoCancellation: true,
        });
        console.log('📞 Microphone changed to device:', deviceId);
      } catch (error) {
        console.error('📞 Failed to set microphone:', error);
      }
    }
  }

  /**
   * Set audio output device (speaker).
   */
  setSpeaker(deviceId: string): void {
    if (this.client) {
      try {
        this.client.setAudioSettings({
          speakerId: deviceId,
        });
        console.log('📞 Speaker changed to device:', deviceId);
      } catch (error) {
        console.error('📞 Failed to set speaker:', error);
      }
    }
  }

  /**
   * Disconnect from Telnyx and cleanup.
   */
  disconnect(): void {
    if (this.currentCall) {
      this.hangup();
    }

    if (this.client) {
      try {
        this.client.disconnect();
        console.log('📞 Disconnected from Telnyx');
      } catch (error) {
        console.error('📞 Failed to disconnect:', error);
      }
      this.client = null;
    }
  }
}
